import {
    Particle,
    IQConfiguration,
    SearchMeta,
    TriageBulkActionRequest,
    TriageBulkActionResponse,
    TriageNlFindRequest,
    TriageNlFindResponse,
    TriageQueryRequest,
    TriageQueryResponse,
} from '@newgenus/common';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, catchError, throwError } from 'rxjs';
import { ErrorUrgency } from '../shared.models';
import { ErrorService } from './error.service';
import { Injectable } from '@angular/core';
import firebase from 'firebase/compat/app';

const suffix = '-default';

/**
 * General API requests
 */
@Injectable()
export class ApiService {
    constructor(
        private errorService: ErrorService,
        private http: HttpClient
    ) {}

    // ******************** Google cloud functions *****************************

    /**
     * @deprecated - pattern is not used anymore.
     */
    public async retrieveIQConfigurations(): Promise<{ message: string; payload: Array<IQConfiguration> }> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        // us-central1-api-integrationsConfigurations-default
        const url = this.getGoogleCloudEnvURL() + `integrationsConfigurations${suffix}/iq`;
        return this.http.get<any>(url, this.getOptions(idToken)).toPromise();
    }

    /** Phase 5a-2: email .ics meeting invites for a calendar event. */
    public async sendEventInvites(body: {
        eventId: string;
        sendFromEmail: string;
        client: 'gmail' | 'outlook';
        cancel?: boolean;
    }): Promise<{ sent: number; sequence: number }> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `calendarSendEventInvites${suffix}`;
        return this.http.post<any>(url, JSON.stringify(body), this.getOptions(idToken)).toPromise();
    }

    /** Phase 5a-2: availability of org users over a time window. */
    public async getFreeBusy(body: {
        emails: string[];
        start: string;
        end: string;
    }): Promise<{ freeBusy: Record<string, { known: boolean; busy: { start: string; end: string }[] }> }> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `calendarGetFreeBusy${suffix}`;
        return this.http.post<any>(url, JSON.stringify(body), this.getOptions(idToken)).toPromise();
    }

    /**
     * OE-126: translate a natural-language sentence into triage filters via
     * `api-triageNlFind-default`. The body carries METADATA ONLY (sender
     * names/addresses, basket size, thread flag) — never subjects or bodies.
     */
    /**
     * OE-150: the Find panel's counts/matches computed by the search index —
     * `api-triageQuery-default`. Callers treat ANY failure as "server find
     * unavailable" and fall back to the client-side compute.
     */
    public async triageQuery(body: TriageQueryRequest): Promise<TriageQueryResponse> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `triageQuery${suffix}`;
        return this.http
            .post<TriageQueryResponse>(url, JSON.stringify(body), this.authOptions(idToken))
            .toPromise() as Promise<TriageQueryResponse>;
    }

    /**
     * OE-151: server-side bulk File / Pend / Mark-read over a key set —
     * `api-triageBulkAction-default`. Select-all's actions run here when
     * server find is live, so unloaded matches are acted on too.
     */
    public async triageBulkAction(body: TriageBulkActionRequest): Promise<TriageBulkActionResponse> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `triageBulkAction${suffix}`;
        return this.http
            .post<TriageBulkActionResponse>(url, JSON.stringify(body), this.authOptions(idToken))
            .toPromise() as Promise<TriageBulkActionResponse>;
    }

    public async triageNlFind(body: TriageNlFindRequest): Promise<TriageNlFindResponse> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `triageNlFind${suffix}`;
        return this.http
            .post<TriageNlFindResponse>(url, JSON.stringify(body), this.authOptions(idToken))
            .toPromise() as Promise<TriageNlFindResponse>;
    }

    public async retrySendParticle(particle: Particle): Promise<{ message: string }> {
        const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
        const url = this.getGoogleCloudEnvURL() + `emailsSendAgain${suffix}`;

        return this.http
            .post<any>(
                url,
                JSON.stringify({ particleKey: particle.particleKey, errorDocId: particle.sendMeta?.errorDocKey }),
                this.getOptions(idToken)
            )
            .toPromise();
    }

    public retrieveGmailAliases(idToken: string, emailAddress: string): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `integrationsRetrieveGmailAliases${suffix}?idToken=${idToken}`;
        return this.http
            .post<object>(url, JSON.stringify({ tokenId: idToken, emailAddress: emailAddress }), this.getOptions())
            .pipe(catchError(this.handleError<any>('integrationsRetrieveGmailAliases')));
    }

    // public sendNewDraftWithUserToken(idToken: string, particle: Particle, shouldSend: boolean): Observable<any> {
    //   const url = this.getGoogleCloudEnvURL() + `draftsSendDraftEmail?idToken=${idToken}`;
    //   return this.http
    //     // tslint:disable-next-line: object-literal-shorthand
    //     .post<any>(url, JSON.stringify({ tokenId: idToken, particle: particle, send: shouldSend }), this.getOptions())
    //     .pipe(
    //       catchError(this.handleError<any>('sendNewDraftWithUserToken')),
    //     );
    // }

    public sendNewDraftAndAttachmentsWithUserToken(
        idToken: string,
        particle: Particle,
        shouldSend: boolean
    ): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `draftsSendDraftEmailWithAttachment${suffix}?idToken=${idToken}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(
                    url,
                    JSON.stringify({ tokenId: idToken, particle: particle, send: shouldSend }),
                    this.getOptions()
                )
                .pipe(catchError(this.handleError<any>('sendNewDraftAndAttachmentsWithUserToken')))
        );
    }

    /**
     * Attachment bytes via the downloadAttachment proxy.
     *
     * This one does NOT go through `handleError`: swallowing to `undefined` made a
     * backend 500 indistinguishable from an empty attachment, so the preview and
     * download paths could only ever say "no data" (G10). The status is logged and
     * rethrown so the caller can show the real reason and offer a retry.
     */
    public downloadAttachment(idToken: string, particle: Partial<Particle>, attachmentId: string): Observable<any> {
        const url =
            this.getGoogleCloudEnvURL() +
            `emailsDownloadAttachment${suffix}?attachmentId=${attachmentId}&messageId=${particle.emailId}&particleType=${particle.particleType}&emailAddress=${particle.clientEmailAddress}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .get<any>(url, this.getCacheOptions(idToken))
                .pipe(
                    catchError((error: unknown) =>
                        throwError(() => this.describeHttpFailure('downloadAttachment', error))
                    )
                )
        );
    }

    /**
     * Log an HTTP failure with its status and return a user-showable Error. Keeping
     * the status in the message is what makes a server error distinguishable from
     * an empty response downstream.
     */
    private describeHttpFailure(operation: string, error: unknown): Error {
        const response = error as { status?: number; statusText?: string; message?: string };
        const status = response?.status;
        console.error(`${operation} failed`, { status, statusText: response?.statusText, error });

        if (status === 0) return new Error('Could not reach the server — check your connection and retry.');
        if (status) return new Error(`Could not fetch attachment (${status}).`);
        return new Error(response?.message || 'Could not fetch attachment.');
    }

    public fetchEmailContent(
        idToken: string,
        messageId: string,
        particleType: string,
        clientEmailAddress: string
    ): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `emailsFetchMessage-content?idToken=${idToken}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(
                    url,
                    JSON.stringify({
                        particleType: particleType,
                        messageId: messageId,
                        emailAddress: clientEmailAddress,
                    }),
                    this.getOptions()
                )
                .pipe(catchError(this.handleError<any>('fetchEmailContent')))
        );
    }

    public fetchEmailAttachments(
        idToken: string,
        messageId: string,
        particleType: string,
        clientEmailAddress: string
    ): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `emailsFetchMessage-attachments?idToken=${idToken}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(
                    url,
                    JSON.stringify({
                        particleType: particleType,
                        messageId: messageId,
                        emailAddress: clientEmailAddress,
                    }),
                    this.getOptions()
                )
                .pipe(catchError(this.handleError<any>('fetchEmailAttachments')))
        );
    }

    // public importEmailWithUserToken(idToken: string, messageId: string): Observable<any> {
    //   const url = this.getGoogleCloudEnvURL() + `syncImportMessage?idToken=${idToken}&gmailId=messageId`;

    //   return this.http
    //     // tslint:disable-next-line: object-literal-shorthand
    //     .post<any>(url, JSON.stringify({ tokenId: idToken, messageId: messageId }), this.getOptions())
    //     .pipe(
    //       catchError(this.handleError<any>('importEmailWithUserToken')),
    //     );
    // }

    public importEmailParticleWithUserToken(idToken: string, particle: Particle): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `syncImportEmailParticle-default`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, JSON.stringify({ particle: particle }), this.authOptions(idToken))
                .pipe(catchError(this.handleError<any>('importEmailParticleWithUserToken')))
        );
    }

    public sendNewEmailWithUserToken(idToken: string, particle: Particle): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `emailsSendNewEmailMessage?idToken=${idToken}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, JSON.stringify({ tokenId: idToken, particle: particle }), this.getOptions())
                .pipe(catchError(this.handleError<any>('sendNewEmailWithUserToken')))
        );
    }

    public searchWithUserToken(idToken: string, searchMeta: SearchMeta): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `searchSearchMessages${suffix}`;
        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, JSON.stringify({ searchMeta: searchMeta }), this.authOptions(idToken))
                .pipe(catchError(this.handleError<any>('searchWithUserToken error!!')))
        );
    }

    /**
     * Server-side search of the user's saved Newgenus particles — returns the
     * COMPLETE set of matches (not just the ~20-per-basket loaded in the browser).
     * Mirrors searchWithUserToken's POST + idToken-query-param contract.
     */
    public searchNewgenusParticles(
        idToken: string,
        searchString: string,
        pageSize?: number,
        cursor?: string | null,
        countOnly?: boolean
    ): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `searchSearchNewgenus${suffix}`;
        const body: { searchString: string; pageSize?: number; cursor?: string; countOnly?: boolean } = {
            searchString,
        };
        if (pageSize != null) body.pageSize = pageSize;
        if (cursor) body.cursor = cursor;
        if (countOnly) body.countOnly = true;
        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, JSON.stringify(body), this.authOptions(idToken))
                .pipe(catchError(this.handleError<any>('searchNewgenusParticles error!!')))
        );
    }

    public setUserClaimsForRegistration(idToken: string): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `adminSetUserClaimsForRegistration${suffix}`;
        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, {}, this.authOptions(idToken))
                .pipe(catchError(this.handleError<any>('setUserClaimsForRegistration error!!')))
        );
    }

    public unlinkFromGmail(idToken: string, gmailEmailAddress: string): Observable<any> {
        const url =
            this.getGoogleCloudEnvURL() +
            `securityToolsInitWatchStop${suffix}?idToken=${idToken}&emailAddress=${gmailEmailAddress}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, {}, this.getOptions())
                .pipe(catchError(this.handleError<any>('unlinkFromGmail')))
        );
    }

    public unlinkMicrosoftAccount(idToken: string, emailAddress: string): Observable<any> {
        const url = this.getGoogleCloudEnvURL() + `securityToolsAzure-oauth/hold-your-horses?idToken=${idToken}`;

        return (
            this.http
                // tslint:disable-next-line: object-literal-shorthand
                .post<any>(url, { emailAddress: emailAddress }, this.getOptions())
                .pipe(catchError(this.handleError<any>('unlinkMicrosoftAccount')))
        );
    }

    // Helper functions
    //

    /**
     * Returns base url for the current environment you are in
     *
     * @TODO move to environment settings
     */
    private getGoogleCloudEnvURL(): string {
        if (environment.useEmulators) {
            return environment.emulator_config.url;
        } else {
            return environment.gcp.baseUrl;
        }
    }

    /**
     * Returns default API request-options
     */
    /**
     * Standard auth options: the ID token rides in the Authorization header
     * instead of a ?idToken= query param (tokens in URLs land in access logs,
     * browser history, and proxies — security hardening Phase 4).
     */
    private authOptions(idToken: string): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`,
            }),
        };
    }

    private getOptions(idToken?: string): { headers: HttpHeaders } {
        return idToken
            ? {
                  headers: new HttpHeaders({
                      'Content-Type': 'application/json',
                      Authentication: idToken,
                  }),
              }
            : {
                  headers: new HttpHeaders({
                      'Content-Type': 'application/json',
                  }),
              };
    }

    /**
     * Returns default API request-options with cache header appended.
     */
    private getCacheOptions(idToken: string): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Cache-Control': 'max-age=259200',
                Authentication: idToken,
                // 3 days: 259200
            }),
        };
    }

    /**
     * Handles API errors
     */
    private handleError<T>(operation: string, urgency = ErrorUrgency.Error, result?: T): (error: any) => Observable<T> {
        return (error: any): Observable<T> => {
            const errorMsg = `Error while fetching ${operation}: ${error.body ? error.body.error : error.message ? error.message : error}`;
            console.error('errorMsg: ', errorMsg);
            if (urgency === ErrorUrgency.Crucial) {
                this.errorService.throwError(errorMsg, urgency);
            }
            return of(result as T);
        };
    }
}
